import { useI18n } from '../contexts/I18nContext';
import { useState } from 'react';

export default function CommunitySection() {
  const { t } = useI18n();
  const [showComingSoon, setShowComingSoon] = useState(false);

  // Sample community stats and posts
  const stats = [
    { number: '15K+', label: t.community.stats.activeMembers },
    { number: '500+', label: t.community.stats.successStories },
    { number: '50+', label: t.community.stats.countries }
  ];

  const recentPosts = t.community.recentPosts;

  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-white overflow-hidden">
      
      {/* Grid Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundSize: '40px 40px',
            backgroundImage: 'linear-gradient(to right, #e4e4e7 1px, transparent 1px), linear-gradient(to bottom, #e4e4e7 1px, transparent 1px)',
          }}
        />
        <div className="absolute inset-0 bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]"></div>
      </div>

      {/* Floating Shapes */}
      <div className="absolute top-24 left-[8%] w-32 h-32 bg-[#9FFFCB] opacity-20 rounded-full blur-2xl animate-float" style={{ animationDuration: '8s' }}></div>
      <div className="absolute bottom-32 right-[12%] w-40 h-40 bg-[#B18AE0]/20 rounded-full blur-2xl animate-float" style={{ animationDuration: '9s', animationDelay: '1.5s' }}></div>

      <div className="container mx-auto px-4 sm:px-8 md:px-12 lg:px-16 relative z-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 space-y-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C2C2C] leading-tight">
            {t.community.title}
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-[#707070] leading-relaxed">
            {t.community.intro}
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-3 gap-4 md:gap-8 mb-12 md:mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="relative p-6 md:p-8 bg-gradient-to-br from-white to-[#F8F5FF] rounded-2xl md:rounded-3xl border-2 border-[#E4E4E7] hover:border-[#9FFFCB] shadow-lg hover:shadow-xl transition-all duration-300">
                {/* Decorative Element */}
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-[#5B3FD0] to-[#B18AE0] rounded-lg opacity-20 rotate-12"></div>
                
                <div className="text-center space-y-2">
                  <p className="text-3xl md:text-5xl font-bold text-[#5B3FD0]">
                    {stat.number}
                  </p>
                  <p className="text-sm md:text-base text-[#707070] font-medium">
                    {stat.label}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 mb-12">
          
          {/* Left - Community Preview */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-2xl md:text-3xl font-bold text-[#2C2C2C]">{t.community.recentDiscussions}</h3>
              <p className="text-[#707070]">{t.community.body}</p>
            </div>

            {/* Discussion Posts */}
            <div className="space-y-3">
              {recentPosts.map((post, index) => (
                <div
                  key={index}
                  className="p-4 md:p-5 bg-white rounded-2xl border border-[#E4E4E7] hover:border-[#9FFFCB] hover:shadow-lg transition-all duration-300 group cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-[#9FFFCB] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-[#2C2C2C] font-bold text-sm">
                          {post.author.charAt(0)}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 space-y-1">
                        <p className="font-bold text-[#2C2C2C] group-hover:text-[#5B3FD0] transition-colors">
                          {post.topic}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-[#707070]">
                          <span className="font-medium">{post.author}</span>
                          <span>•</span>
                          <span>{post.time}</span>
                        </div>
                      </div>
                    </div>

                    {/* Replies Badge */}
                    <div className="flex items-center gap-1 px-3 py-1 bg-[#F8F5FF] rounded-full">
                      <svg className="w-4 h-4 text-[#5B3FD0]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414z"/>
                      </svg>
                      <span className="text-xs font-bold text-[#5B3FD0]">{post.replies}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Community Benefits */}
          <div className="relative">
            <div className="sticky top-8 space-y-6">
              
              {/* Benefits Card */}
              <div className="relative bg-[#5B3FD0] rounded-3xl p-8 md:p-10 shadow-2xl overflow-hidden">
                {/* Decorative Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                    backgroundSize: '30px 30px'
                  }}></div>
                </div>

                {/* Floating Decorative Elements */}
                <div className="absolute top-8 right-8 w-24 h-24 bg-white/10 rounded-full animate-float" style={{ animationDuration: '6s' }}></div>
                <div className="absolute bottom-8 left-8 w-16 h-16 bg-white/10 rounded-2xl rotate-45"></div>

                <div className="relative space-y-6">
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    {t.community.whatYouWillGet}
                  </h3>

                  <ul className="space-y-4">
                    {t.community.benefits.map((benefit: string, index: number) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-[#9FFFCB] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-4 h-4 text-[#2C2C2C]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <span className="text-white text-base md:text-lg">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4">
                    <button 
                      onClick={() => setShowComingSoon(true)}
                      className="w-full px-6 py-4 bg-white text-[#5B3FD0] rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                    >
                      {t.community.cta}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Highlight */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative px-6 md:px-12 py-8 bg-gradient-to-r from-[#F0FDFA] to-[#F8F5FF] rounded-3xl border-2 border-[#9FFFCB]/30 shadow-xl">
            {/* Decorative Dots */}
            <div className="absolute top-4 left-4 flex gap-2">
              <div className="w-2 h-2 bg-[#5B3FD0] rounded-full"></div>
              <div className="w-2 h-2 bg-[#B18AE0] rounded-full"></div>
              <div className="w-2 h-2 bg-[#9FFFCB] rounded-full"></div>
            </div>
            
            <p className="text-center text-lg md:text-2xl font-bold text-[#2C2C2C] pt-4 md:pt-0">
              {t.community.highlight}
            </p>

            {/* Decorative Corner */}
            <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-[#5B3FD0] rounded-tl-3xl opacity-10"></div>
          </div>
        </div>
      </div>

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowComingSoon(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#5B3FD0]/10 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-[#5B3FD0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-[#2C2C2C]">{t.community.comingSoon}</h3>
              <p className="text-[#707070]">{t.community.comingSoonMsg}</p>
              <button
                onClick={() => setShowComingSoon(false)}
                className="w-full px-6 py-3 bg-[#5B3FD0] text-white rounded-xl font-medium hover:bg-[#4729b0] transition-colors"
              >
                {t.community.gotIt}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
